import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const contactChannels = [
  { icon: 'ri-phone-line', label: '客服热线', value: '400-888-8888', sub: '周一至周日 8:00–22:00', color: 'bg-red-50 text-red-600', action: '立即拨打' },
  { icon: 'ri-mail-line', label: '电子邮箱', value: 'contact@jinbangwangcai.com', sub: '1–2个工作日内回复', color: 'bg-amber-50 text-amber-600', action: '发送邮件' },
  { icon: 'ri-wechat-line', label: '微信公众号', value: '金榜旺财升学平台', sub: '关注后发送消息咨询', color: 'bg-green-50 text-green-600', action: '扫码关注' },
  { icon: 'ri-map-pin-line', label: '办公地址', value: '北京市海淀区中关村教育园区A座8楼', sub: '欢迎预约到访', color: 'bg-pink-50 text-pink-600', action: '查看地图' },
];

const faqs = [
  { q: '如何购买教学视频？', a: '在视频详情页点击「立即购买」，选择支付方式完成支付后即可永久观看。也可先点击「免费试听3分钟」体验课程质量。' },
  { q: '如何成为平台认证教师？', a: '请发送简历和教学资质证明至 teacher@jinbangwangcai.com，我们的教研团队会在3个工作日内与您联系。' },
  { q: '如何找到城乡学伴？', a: '进入「社区」页面，点击「城乡学伴配对」，填写申请表单，系统会在24小时内为您智能匹配最合适的学伴。' },
  { q: '购买的课程可以退款吗？', a: '课程购买后7天内，如未观看超过30%，可申请全额退款。请联系客服热线或发送邮件说明退款原因。' },
  { q: '乡村学生有优惠政策吗？', a: '是的！来自农村地区的学生可申请「助学优惠」，部分课程享受5折优惠。请联系客服提供相关证明材料。' },
];

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const contactSchema = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  '@id': `${SITE_URL}/contact`,
  url: `${SITE_URL}/contact`,
  name: '联系我们 - 金榜旺财客服中心',
  description: '金榜旺财客服中心，提供课程咨询、学伴配对、教师合作、助学优惠等服务，7×24小时随时为您服务。',
  inLanguage: 'zh-CN',
  isPartOf: { '@id': `${SITE_URL}/#website` },
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: '联系我们', item: `${SITE_URL}/contact` },
    ],
  },
};

export default function ContactPage() {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  // Online consultation
  const [consultForm, setConsultForm] = useState({ name: '', email: '', phone: '', type: '', question: '' });
  const [consultSubmitting, setConsultSubmitting] = useState(false);
  const [consultSubmitted, setConsultSubmitted] = useState(false);
  const [consultError, setConsultError] = useState('');

  useEffect(() => {
    const id = 'schema-contact';
    let el = document.getElementById(id) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement('script');
      el.id = id;
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(contactSchema);
    document.title = '联系我们 - 金榜旺财客服中心';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', '金榜旺财客服中心，提供课程咨询、学伴配对、教师合作、助学优惠等服务，7×24小时随时为您服务。');
    return () => { const s = document.getElementById(id); if (s) s.remove(); };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.message.length > 500) { setError('留言不能超过500字'); return; }
    setSubmitting(true); setError('');
    try {
      const body = new URLSearchParams({ name: formData.name, email: formData.email, subject: formData.subject, message: formData.message });
      await fetch('https://readdy.ai/api/form/d7cp56um3fsfopf23hl0', { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: body.toString() });
      setSubmitted(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch { setError('提交失败，请稍后重试'); }
    finally { setSubmitting(false); }
  };

  const handleConsultSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (consultForm.question.length > 500) { setConsultError('问题描述不能超过500字'); return; }
    setConsultSubmitting(true); setConsultError('');
    try {
      const body = new URLSearchParams({ name: consultForm.name, email: consultForm.email, phone: consultForm.phone, type: consultForm.type, question: consultForm.question });
      await fetch('https://readdy.ai/api/form/d7cq4uvsch5bvrijf7ug', { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: body.toString() });
      setConsultSubmitted(true);
      setConsultForm({ name: '', email: '', phone: '', type: '', question: '' });
    } catch { setConsultError('提交失败，请稍后重试'); }
    finally { setConsultSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#faf9f7]">
      <Navbar />

      {/* Header */}
      <div className="relative pt-20 pb-16 bg-gradient-to-br from-pink-900 via-green-900 to-pink-800 overflow-hidden">
        <div className="absolute -top-16 -left-16 w-64 h-64 bg-pink-500/20 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute -bottom-10 right-0 w-64 h-64 bg-green-500/20 rounded-full blur-3xl pointer-events-none"></div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-amber-500/20 border border-amber-400/40 rounded-full px-4 py-1.5 mb-5">
            <i className="ri-customer-service-line text-amber-400 text-sm"></i>
            <span className="text-amber-300 text-sm font-medium">7×24小时 · 随时为你服务</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-black text-white mb-3">联系我们</h1>
          <p className="text-white/70 text-base max-w-md mx-auto">有任何问题或建议，欢迎随时联系我们。我们的团队随时准备为你提供帮助。</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-8 py-14">

        {/* Contact channels */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {contactChannels.map((ch) => (
            <div key={ch.label} className="bg-white rounded-2xl p-5 hover:scale-[1.02] transition-all">
              <div className={`w-11 h-11 flex items-center justify-center rounded-xl mb-4 ${ch.color}`}>
                <i className={`${ch.icon} text-xl`}></i>
              </div>
              <p className="text-xs text-gray-400 mb-0.5">{ch.label}</p>
              <p className="text-sm font-bold text-gray-900 mb-1 leading-snug">{ch.value}</p>
              <p className="text-xs text-gray-400 mb-3">{ch.sub}</p>
              <button className={`text-xs font-semibold flex items-center gap-1 cursor-pointer ${ch.color.split(' ')[1]} hover:opacity-80 transition-opacity`}>
                {ch.action} <i className="ri-arrow-right-s-line"></i>
              </button>
            </div>
          ))}
        </div>

        {/* Online Consultation + Message Form */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-14">

          {/* Online Consultation */}
          <div className="bg-white rounded-2xl p-7">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-xl">
                <i className="ri-chat-smile-2-line text-green-600 text-lg"></i>
              </div>
              <div>
                <h2 className="text-lg font-black text-gray-900">在线咨询</h2>
                <p className="text-gray-400 text-xs">填写问题，我们优先回复</p>
              </div>
              <div className="ml-auto flex items-center gap-1.5 bg-green-50 border border-green-200 rounded-full px-3 py-1">
                <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-green-700 text-xs font-medium">在线</span>
              </div>
            </div>

            {consultSubmitted ? (
              <div className="text-center py-10">
                <div className="w-14 h-14 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                  <i className="ri-checkbox-circle-line text-green-600 text-2xl"></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">咨询已提交！</h3>
                <p className="text-gray-500 text-sm mb-5">我们会在30分钟内通过邮件或电话与您联系。</p>
                <button onClick={() => setConsultSubmitted(false)} className="px-6 py-2.5 bg-green-600 text-white rounded-full text-sm font-semibold cursor-pointer hover:bg-green-700 transition-colors whitespace-nowrap">
                  再次咨询
                </button>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleConsultSubmit} className="flex flex-col gap-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">姓名 *</label>
                    <input required value={consultForm.name} onChange={(e) => setConsultForm({ ...consultForm, name: e.target.value })} placeholder="您的姓名" className="w-full bg-gray-50 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-200" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">手机号</label>
                    <input value={consultForm.phone} onChange={(e) => setConsultForm({ ...consultForm, phone: e.target.value })} placeholder="方便联系您" className="w-full bg-gray-50 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-200" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">邮箱 *</label>
                  <input required type="email" name="email" value={consultForm.email} onChange={(e) => setConsultForm({ ...consultForm, email: e.target.value })} placeholder="用于接收回复" className="w-full bg-gray-50 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-200" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">咨询类型 *</label>
                  <div className="flex flex-wrap gap-2">
                    {['课程购买', '学伴配对', '教师合作', '助学优惠', '技术问题', '其他'].map((t) => (
                      <button key={t} type="button" onClick={() => setConsultForm({ ...consultForm, type: t })}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all cursor-pointer whitespace-nowrap ${consultForm.type === t ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}>
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">问题描述 * <span className="text-gray-400 font-normal">（最多500字）</span></label>
                  <textarea required value={consultForm.question} onChange={(e) => { if (e.target.value.length <= 500) setConsultForm({ ...consultForm, question: e.target.value }); }} placeholder="请详细描述您的问题，我们会尽快为您解答..." rows={4} className="w-full bg-gray-50 rounded-xl px-3 py-2.5 text-sm outline-none resize-none focus:ring-2 focus:ring-green-200" />
                  <p className="text-right text-xs text-gray-400 mt-0.5">{consultForm.question.length}/500</p>
                </div>
                {consultError && <p className="text-red-500 text-xs">{consultError}</p>}
                <button type="submit" disabled={consultSubmitting} className="w-full py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-full cursor-pointer transition-colors whitespace-nowrap disabled:opacity-60">
                  {consultSubmitting ? '提交中...' : '提交咨询'}
                </button>
              </form>
            )}
          </div>

          {/* Message Form */}
          <div className="bg-white rounded-2xl p-7">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center bg-red-100 rounded-xl">
                <i className="ri-mail-send-line text-red-600 text-lg"></i>
              </div>
              <div>
                <h2 className="text-lg font-black text-gray-900">发送消息</h2>
                <p className="text-gray-400 text-xs">1–2个工作日内回复</p>
              </div>
            </div>

            {submitted ? (
              <div className="text-center py-10">
                <div className="w-14 h-14 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                  <i className="ri-checkbox-circle-line text-green-600 text-2xl"></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">消息已发送！</h3>
                <p className="text-gray-500 text-sm mb-5">我们会在1–2个工作日内回复您，感谢您的联系！</p>
                <button onClick={() => setSubmitted(false)} className="px-6 py-2.5 bg-red-600 text-white rounded-full text-sm font-semibold cursor-pointer hover:bg-red-700 transition-colors whitespace-nowrap">
                  再次发送
                </button>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">姓名 *</label>
                    <input required type="text" name="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="您的姓名" className="w-full bg-gray-50 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-red-200" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">邮箱 *</label>
                    <input required type="email" name="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="您的邮箱" className="w-full bg-gray-50 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-red-200" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">主题</label>
                  <select name="subject" value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} className="w-full bg-gray-50 rounded-xl px-3 py-2.5 text-sm outline-none cursor-pointer">
                    <option value="">请选择主题</option>
                    <option value="课程咨询">课程咨询</option>
                    <option value="教师合作">教师合作</option>
                    <option value="商家入驻">商家入驻</option>
                    <option value="技术问题">技术问题</option>
                    <option value="助学申请">助学申请</option>
                    <option value="其他">其他</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">留言 * <span className="text-gray-400 font-normal">（最多500字）</span></label>
                  <textarea required name="message" value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} placeholder="请输入您的问题或建议..." rows={5} maxLength={500} className="w-full bg-gray-50 rounded-xl px-3 py-2.5 text-sm outline-none resize-none focus:ring-2 focus:ring-red-200" />
                  <p className="text-right text-xs text-gray-400 mt-0.5">{formData.message.length}/500</p>
                </div>
                {error && <p className="text-red-500 text-xs">{error}</p>}
                <button type="submit" disabled={submitting} className="w-full py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-full cursor-pointer transition-colors whitespace-nowrap disabled:opacity-60">
                  {submitting ? '发送中...' : '发送消息'}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* FAQ */}
        <div className="mb-14">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-black text-gray-900 mb-2">常见问题</h2>
            <p className="text-gray-500 text-sm">找不到答案？随时联系我们的客服团队</p>
          </div>
          <div className="flex flex-col gap-3 max-w-3xl mx-auto">
            {faqs.map((faq, idx) => (
              <div key={idx} className="bg-white rounded-2xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900 text-sm pr-4">{faq.q}</span>
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className={`${openFaq === idx ? 'ri-subtract-line' : 'ri-add-line'} text-gray-400 text-base transition-transform`}></i>
                  </div>
                </button>
                {openFaq === idx && (
                  <div className="px-6 pb-5">
                    <p className="text-gray-500 text-sm leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Map placeholder */}
        <div className="bg-white rounded-2xl overflow-hidden">
          <div className="p-5 border-b border-gray-50 flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-pink-100 rounded-xl">
              <i className="ri-map-pin-line text-pink-600 text-base"></i>
            </div>
            <div>
              <p className="font-bold text-gray-900 text-sm">办公地址</p>
              <p className="text-gray-400 text-xs">北京市海淀区中关村教育园区A座8楼</p>
            </div>
          </div>
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3059.123456789!2d116.3097!3d40.0024!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x35f0531b3b3b3b3b%3A0x3b3b3b3b3b3b3b3b!2z5Lit5YyX5YyX5YyX!5e0!3m2!1szh-CN!2scn!4v1234567890"
            width="100%"
            height="280"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="办公地址地图"
          ></iframe>
        </div>

      </div>

      <Footer />
    </div>
  );
}
